import { makeObservable, computed } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { Color, GraphNodeConstructorProps, GraphNodeProps } from "@corca/data";

export class GraphNodeModel extends BaseNodeModel<GraphNodeProps> implements VisitableNode {
  static type = "graph" as const;

  type = GraphNodeModel.type;

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<GraphNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      graphExpressionNodes: computed,
      expressionColors: computed,
    });
  }

  protected fillRequiredProps(props: GraphNodeConstructorProps): GraphNodeProps {
    return props;
  }

  static create(doc: DocumentModel, nodes?: GraphExpressionNodeModel[], id?: NodeId) {
    return new GraphNodeModel(doc, id, {}, nodes);
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitGraphNode(this);
  }

  /** Rendered inside a locked block; see `EditorModel.lockedRootIds`. */
  get locked(): boolean {
    return this.doc.editor.lockedRootIds.has(this.id);
  }

  get graphExpressionNodes(): GraphExpressionNodeModel[] {
    return this.nodes.filter((n) => n.is(GraphExpressionNodeModel));
  }

  get expressionColors(): (Color | undefined)[] {
    return this.graphExpressionNodes.map((n) => n.color);
  }
}
